import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { toast } from 'sonner';
import { Mail, Lock, LogIn, X } from 'lucide-react';

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await login(formData.email, formData.password);

    if (result.success) {
      toast.success('Login successful! Welcome back.');
      navigate('/');
    } else {
      toast.error(result.error);
    }

    setLoading(false);
  };

  return (
    <Dialog open={true} onOpenChange={() => navigate('/')}>
      <DialogContent className="max-w-md p-0 gap-0 flex flex-col" hideCloseButton={true}>
        {/* Fixed Header */}
        <DialogHeader className="px-6 py-3 border-b flex-shrink-0 relative flex items-center">
          <DialogTitle className="text-lg text-center flex-1">Login</DialogTitle>
          <button
            onClick={() => navigate('/')}
            className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center justify-center w-8 h-8 hover:bg-gray-100 rounded transition-colors"
          >
            <X className="h-5 w-5 text-gray-600" />
          </button>
        </DialogHeader>

        {/* Form Content */}
        <div className="px-6 py-4">
          <form id="login-form" onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-sm">Email Address</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="your@email.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="pl-9 h-9 text-sm"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-sm">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                <Input
                  id="password"
                  name="password"
                  type="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className="pl-9 h-9 text-sm"
                />
              </div>
            </div>
          </form>
        </div>

        {/* Fixed Footer */}
        <DialogFooter className="px-6 py-4 border-t flex-shrink-0 flex-col gap-3 sm:flex-col">
          <Button
            type="submit"
            form="login-form"
            disabled={loading}
            className="w-full bg-orange-500 hover:bg-orange-600 text-white h-9 text-sm"
          >
            {loading ? (
              'Signing in...'
            ) : (
              <>
                <LogIn className="mr-2 h-4 w-4" />
                Sign In
              </>
            )}
          </Button>

          <div className="text-center text-xs">
            <span className="text-gray-600">Don't have an account? </span>
            <Link to="/register" className="text-orange-500 hover:text-orange-600 font-medium">
              Sign up
            </Link>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default Login;
